import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { api, extractApiError } from '@/lib/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

const forgotSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

type ForgotValues = z.infer<typeof forgotSchema>;

export default function ForgotPassword() {
  const form = useForm<ForgotValues>({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: '' },
  });

  const mutation = useMutation({
    mutationFn: async (values: ForgotValues) => {
      // Backend always returns 200 regardless of whether the email exists
      // (anti-enumeration). We treat any 2xx as success.
      await api.post('/auth/forgot-password', values);
    },
    onError: (err) => {
      const apiErr = extractApiError(err);
      if (apiErr?.code === 'auth.rate_limited') {
        toast.error('Too many requests. Please try again later.');
      } else {
        toast.error(apiErr?.message || 'Could not send reset link. Please try again.');
      }
    },
  });

  const onSubmit = (values: ForgotValues) => {
    mutation.mutate(values);
  };

  const isPending = mutation.isPending;

  if (mutation.isSuccess) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Check your inbox
          </CardTitle>
          <CardDescription>
            If an account exists for that email, we sent a password reset link. It expires in
            1 hour.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            Didn't get it? Check your spam folder, or wait a minute and try again. Still stuck?
            Email{' '}
            <a className="text-foreground underline" href="mailto:founder@dagsight.com">
              founder@dagsight.com
            </a>
            .
          </p>
        </CardContent>
        <CardFooter>
          <Button asChild variant="outline" className="w-full">
            <Link to="/sign-in">Back to sign in</Link>
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reset password</CardTitle>
        <CardDescription>Enter your email and we'll send you a reset link.</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? 'Sending…' : 'Send reset link'}
            </Button>
            <div className="text-sm text-muted-foreground">
              <Link to="/sign-in" className="hover:underline">
                Back to sign in
              </Link>
            </div>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
